import { useMemo, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import Modal from '@/components/Modal/Modal';
import Button from '@/components/Button/Button';
import { useBasketContext } from '@/contexts/BasketContext';
import { useShareBasket } from '@/hooks/useShareBasket';
import {
  SharedBasketExpiry,
  SharedBasketItemCategory,
  SharedBasketRecipientMode,
} from '@/lib/types';
import RecipientPicker, { SelectedPartner } from './RecipientPicker';
import { useBasketPreview } from './useBasketPreview';
import ExpirySelect from './ExpirySelect';
import { expiryLabel } from './ExpirySelectHelpers';
import { formatSharedBasketDate } from '@/lib/sharedBasketDate';
import DisableLinkConfirm, {
  DangerButton,
} from './DisableLinkConfirm';

const CategoryNames: Record<SharedBasketItemCategory, string> = {
  compliance_documents: 'compliance',
  marketing_assets: 'marketing',
};

const ShareBasketModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { items, count, clear, removeMany } = useBasketContext();
  const {
    basket,
    create,
    isCreating,
    createError,
    disable,
    isDisabling,
    disableError,
    clearDisableError,
    reset,
  } = useShareBasket();

  const [mode, setMode] =
    useState<SharedBasketRecipientMode>('partner');
  const [partner, setPartner] = useState<SelectedPartner | null>(null);
  const [contactUserIds, setContactUserIds] = useState<number[]>([]);
  const [expiry, setExpiry] = useState<SharedBasketExpiry>('1_week');
  const [copied, setCopied] = useState(false);
  const [confirmingDisable, setConfirmingDisable] = useState(false);

  const isDisabled = basket?.status === 'disabled';
  const shareUrl = basket
    ? `${window.location.origin}/shared/${basket.token}`
    : '';

  // What the server says these files are. Only while step 1 is on screen -
  // once the basket exists, the create response describes it.
  const {
    data: preview,
    isPending: isPreviewPending,
    isError: isPreviewError,
  } = useBasketPreview(items, open && !basket);
  // A disabled query reports `isPending` forever, so pair it with the reason
  // it would be enabled - otherwise an empty basket shows "Checking…" for good.
  const isCheckingFiles = isPreviewPending && items.length > 0;
  const basketFiles = useMemo(
    () =>
      basket
        ? (
            Object.keys(CategoryNames) as SharedBasketItemCategory[]
          ).flatMap((category) =>
            basket.files[category].map((file) => ({
              category,
              file,
            })),
          )
        : [],
    [basket],
  );

  const unavailableKeys = useMemo(
    () =>
      new Set(
        (preview?.unavailable ?? []).map(
          (item) => `${item.category}:${item.file_id}`,
        ),
      ),
    [preview],
  );

  const previewNames = useMemo(() => {
    const names = new Map<string, string>();
    if (!preview) return names;
    (Object.keys(CategoryNames) as SharedBasketItemCategory[]).forEach(
      (category) =>
        preview.files[category].forEach((file) =>
          names.set(`${category}:${file.id}`, file.name),
        ),
    );
    return names;
  }, [preview]);

  const handleRemoveUnavailable = () => {
    (Object.keys(CategoryNames) as SharedBasketItemCategory[]).forEach(
      (category) => {
        const ids = (preview?.unavailable ?? [])
          .filter((item) => item.category === category)
          .map((item) => item.file_id);
        if (ids.length) removeMany(category, ids);
      },
    );
  };

  const breakdown = useMemo(() => {
    const source = basket
      ? basketFiles.map((entry) => entry.category)
      : items.map((item) => item.category);
    return (
      Object.keys(CategoryNames) as SharedBasketItemCategory[]
    )
      .map((category) => ({
        category,
        count: source.filter((value) => value === category).length,
      }))
      .filter((entry) => entry.count > 0)
      .map((entry) => `${entry.count} ${CategoryNames[entry.category]}`)
      .join(' · ');
  }, [basket, basketFiles, items]);

  const recipientsValid =
    mode === 'partner'
      ? partner != null
      : contactUserIds.length > 0;

  // Create 403s the *whole* basket if any one file is unshareable, so letting
  // Generate through here buys a guaranteed failure. A preview that itself
  // failed is not evidence of anything - don't block on it.
  const hasUnavailable = !!preview?.unavailable.length;
  const canGenerate =
    recipientsValid &&
    !hasUnavailable &&
    (!isCheckingFiles || isPreviewError) &&
    items.length > 0;

  const handleClose = () => {
    // Creation can take tens of seconds; closing mid-flight would mint a live
    // link the user never sees the URL for.
    if (isCreating || isDisabling) return;
    // The snapshot now lives server-side; leaving it checked invites an
    // accidental duplicate share.
    if (basket) clear();
    reset();
    setMode('partner');
    setPartner(null);
    setContactUserIds([]);
    setExpiry('1_week');
    setCopied(false);
    setConfirmingDisable(false);
    onClose();
  };

  const handleGenerate = () => {
    if (!canGenerate || isCreating) return;
    create({
      items,
      recipient_mode: mode,
      // Exactly one of these; sending both is a 422 from check_recipients.
      ...(mode === 'partner'
        ? { partner_id: partner!.id }
        : { contact_user_ids: contactUserIds }),
      expiry,
    });
  };

  const handleCopy = () => {
    navigator.clipboard?.writeText(shareUrl).then(
      () => setCopied(true),
      () => setCopied(false),
    );
  };

  const step1Actions = (
    <>
      <Button
        variant="text"
        onClick={handleClose}
        disabled={isCreating}
        aria-label="Cancel sharing"
      >
        <FormattedMessage id="cancel" defaultMessage="Cancel" />
      </Button>
      <Button
        variant="contained"
        onClick={handleGenerate}
        disabled={!canGenerate || isCreating}
        loading={isCreating}
        aria-label="Generate share link"
      >
        <FormattedMessage
          id="generateShareLink"
          defaultMessage="Generate share link"
        />
      </Button>
    </>
  );

  const step2Actions = (
    <>
      {basket?.can_disable && !confirmingDisable && (
        <DangerButton
          variant="outlined"
          onClick={() => {
            clearDisableError();
            setConfirmingDisable(true);
          }}
          aria-label="Disable link"
        >
          <FormattedMessage
            id="disableLink"
            defaultMessage="Disable link"
          />
        </DangerButton>
      )}
      <Spacer />
      <Button
        variant="contained"
        onClick={handleClose}
        aria-label="Done"
      >
        <FormattedMessage id="done" defaultMessage="Done" />
      </Button>
    </>
  );

  return (
    <StyledModal
      open={open}
      onClose={handleClose}
      isActionBusy={isCreating || isDisabling}
      title={
        basket ? (
          isDisabled ? (
            <FormattedMessage
              id="linkDisabledTitle"
              defaultMessage="Link disabled"
            />
          ) : (
            <FormattedMessage
              id="shareLinkGenerated"
              defaultMessage="Share link generated"
            />
          )
        ) : (
          <FormattedMessage
            id="shareNFiles"
            defaultMessage="Share {count, plural, one {# file} other {# files}}"
            values={{ count }}
          />
        )
      }
      actions={basket ? step2Actions : step1Actions}
      actionsError={createError}
    >
      {basket ? (
        <>
          <Sub>
            {isDisabled ? (
              <FormattedMessage
                id="linkDisabledSub"
                defaultMessage="Nobody can open this link any more, including the people it was shared with. To share these files again, create a new basket with the right recipients."
              />
            ) : (
              <FormattedMessage
                id="shareLinkGeneratedSub"
                defaultMessage="Only the recipients below can open this link. Anyone else who opens it — even signed in to the portal — sees nothing."
              />
            )}
          </Sub>

          <LinkRow>
            <LinkInput
              readOnly
              value={shareUrl}
              disabled={isDisabled}
              $dead={isDisabled}
              aria-label="Share link"
            />
            <Button
              variant="contained"
              onClick={handleCopy}
              disabled={isDisabled}
              aria-label="Copy link"
            >
              {isDisabled ? (
                <FormattedMessage
                  id="linkDisabledShort"
                  defaultMessage="Disabled"
                />
              ) : copied ? (
                <FormattedMessage
                  id="copied"
                  defaultMessage="Copied ✓"
                />
              ) : (
                <FormattedMessage
                  id="copyLink"
                  defaultMessage="Copy link"
                />
              )}
            </Button>
          </LinkRow>

          <Recap>
            <RecapRow>
              <RecapKey>
                <FormattedMessage
                  id="sharedWith"
                  defaultMessage="Shared with"
                />
              </RecapKey>
              <RecapValue>
                {mode === 'partner' ? (
                  (partner?.name ?? (
                    <FormattedMessage
                      id="recipientModePartner"
                      defaultMessage="A partner"
                    />
                  ))
                ) : (
                  <FormattedMessage
                    id="recipientSummaryContacts"
                    defaultMessage="{count, plural, one {# contact} other {# contacts}}"
                    values={{ count: contactUserIds.length }}
                  />
                )}
              </RecapValue>
            </RecapRow>
            <RecapRow>
              <RecapKey>
                <FormattedMessage id="files" defaultMessage="Files" />
              </RecapKey>
              <RecapValue>
                <FormattedMessage
                  id="filesRecap"
                  defaultMessage="{count, plural, one {# file} other {# files}}"
                  values={{ count: basketFiles.length }}
                />
                {breakdown ? ` · ${breakdown}` : ''}
              </RecapValue>
            </RecapRow>
            <RecapRow>
              <RecapKey>
                <FormattedMessage
                  id="expires"
                  defaultMessage="Expires"
                />
              </RecapKey>
              <RecapValue>
                {isDisabled ? (
                  <Dead>
                    <FormattedMessage
                      id="disabledNoLongerValid"
                      defaultMessage="Disabled — no longer valid"
                    />
                  </Dead>
                ) : (
                  <>
                    {formatSharedBasketDate(basket.expires_at)}
                    <Muted>{` · in ${expiryLabel(expiry)}`}</Muted>
                  </>
                )}
              </RecapValue>
            </RecapRow>
          </Recap>

          {confirmingDisable && !isDisabled && (
            <DisableLinkConfirm
              error={disableError}
              isDisabling={isDisabling}
              onConfirm={() => disable(basket.token)}
              onCancel={() => setConfirmingDisable(false)}
            />
          )}

          {!isDisabled && (
            <Caption>
              <FormattedMessage
                id="shareLinkCaption"
                defaultMessage="Permissions are re-checked for each recipient when the link is opened. Files a recipient cannot access — or that no longer exist — are left out of their basket."
              />
            </Caption>
          )}
        </>
      ) : (
        <>
          <Sub>
            <FormattedMessage
              id="shareBasketSub"
              defaultMessage="Choose who this link is for. The link is only an address to the basket — it does not grant access, and each recipient still sees only the files their permissions allow."
            />
          </Sub>

          <RecipientPicker
            mode={mode}
            onModeChange={setMode}
            partner={partner}
            onPartnerChange={setPartner}
            contactUserIds={contactUserIds}
            onContactUserIdsChange={setContactUserIds}
            disabled={isCreating}
          />

          <ExpirySelect
            value={expiry}
            onChange={setExpiry}
            disabled={isCreating}
          />

          <FieldLabel>
            <FormattedMessage
              id="filesInThisBasket"
              defaultMessage="Files in this basket"
            />
          </FieldLabel>
          <FileList>
            {items.map((item) => {
              const key = `${item.category}:${item.file_id}`;
              const name = previewNames.get(key);
              const isUnavailable = unavailableKeys.has(key);
              return (
                <FileRow key={key} $dimmed={isUnavailable}>
                  {/* Falls back to the id while the names are in flight, and
                      for anything the server would not name - an unavailable
                      file is never named, which is the point of it being
                      unavailable. */}
                  <FileName title={name}>
                    {name ?? `#${item.file_id}`}
                  </FileName>
                  <Muted>
                    {isUnavailable ? (
                      <FormattedMessage
                        id="fileNoLongerAvailable"
                        defaultMessage="No longer available"
                      />
                    ) : (
                      CategoryNames[item.category]
                    )}
                  </Muted>
                </FileRow>
              );
            })}
            {isCheckingFiles && (
              <FileRow $dimmed={false}>
                <PreviewLoading>
                  <CircularProgress size={14} />
                  <FormattedMessage
                    id="checkingFiles"
                    defaultMessage="Checking these files…"
                  />
                </PreviewLoading>
              </FileRow>
            )}
          </FileList>

          {hasUnavailable && (
            <UnavailableNotice role="status">
              <FormattedMessage
                id="basketHasUnavailableFiles"
                defaultMessage="{count, plural, one {# file} other {# files}} in this basket can no longer be shared — it may have been deleted, or your access to it changed. Sharing fails until they are removed."
                values={{ count: preview?.unavailable.length ?? 0 }}
              />
              <Button
                variant="text"
                onClick={handleRemoveUnavailable}
                aria-label="Remove unavailable files from the basket"
              >
                <FormattedMessage
                  id="removeThem"
                  defaultMessage="Remove them"
                />
              </Button>
            </UnavailableNotice>
          )}

          <Caption>
            <FormattedMessage
              id="shareBasketSnapshotCaption"
              defaultMessage="The basket is saved as a snapshot of these files — it will not change if the underlying search results do."
            />
          </Caption>
        </>
      )}
    </StyledModal>
  );
};

const StyledModal = styled(Modal)({
  width: '90%',
  maxWidth: 560,
});

const Sub = styled('p')(({ theme }) => ({
  margin: 0,
  marginBottom: theme.spacing(2),
  fontSize: 13,
  lineHeight: '18px',
  color: theme.palette.grey500,
}));

const FieldLabel = styled('div')(({ theme }) => ({
  fontSize: 13,
  fontWeight: 600,
  color: theme.palette.grey600,
  marginTop: theme.spacing(2.5),
  marginBottom: theme.spacing(1),
}));

const FileList = styled('div')(({ theme }) => ({
  maxHeight: 160,
  overflowY: 'auto',
  border: `1px solid ${theme.palette.grey200}`,
  borderRadius: theme.radii.sm,
}));

const FileRow = styled('div', {
  shouldForwardProp: (prop) => prop !== '$dimmed',
})<{ $dimmed: boolean }>(({ theme, $dimmed }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 1.5),
  fontSize: 13,
  opacity: $dimmed ? 0.55 : 1,
  '&:not(:last-of-type)': {
    borderBottom: `1px solid ${theme.palette.grey100}`,
  },
}));

const FileName = styled('span')(({ theme }) => ({
  color: theme.palette.grey900,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const PreviewLoading = styled('span')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  color: theme.palette.grey400,
}));

const UnavailableNotice = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.radii.sm,
  border: `1px solid ${theme.palette.yellow500}`,
  background: theme.palette.yellow50,
  color: theme.palette.grey900,
  fontSize: 12,
  lineHeight: '16px',
}));

const Caption = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontSize: 12,
  lineHeight: '16px',
  color: theme.palette.grey400,
}));

const LinkRow = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
}));

const LinkInput = styled('input', {
  shouldForwardProp: (prop) => prop !== '$dead',
})<{ $dead: boolean }>(({ theme, $dead }) => ({
  flexGrow: 1,
  minWidth: 0,
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.radii.sm,
  border: `1px solid ${theme.palette.grey300}`,
  background: $dead ? theme.palette.grey100 : theme.palette.white,
  color: $dead ? theme.palette.grey400 : theme.palette.grey900,
  textDecoration: $dead ? 'line-through' : 'none',
  fontSize: 13,
}));

const Recap = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(2),
  border: `1px solid ${theme.palette.grey200}`,
  borderRadius: theme.radii.sm,
}));

const RecapRow = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  padding: theme.spacing(1, 1.5),
  fontSize: 13,
  '&:not(:last-of-type)': {
    borderBottom: `1px solid ${theme.palette.grey100}`,
  },
}));

const RecapKey = styled('div')(({ theme }) => ({
  color: theme.palette.grey500,
}));

const RecapValue = styled('div')(({ theme }) => ({
  color: theme.palette.grey900,
  fontWeight: 600,
  textAlign: 'right',
}));

const Muted = styled('span')(({ theme }) => ({
  color: theme.palette.grey400,
  fontWeight: 500,
}));

const Dead = styled('span')(({ theme }) => ({
  color: theme.palette.red600,
}));







const Spacer = styled('div')({ flexGrow: 1 });

export default ShareBasketModal;
